import { useNavigate } from "react-router-dom";
import type { Id } from "../../../../convex/_generated/dataModel";
import {
  FolderIcon,
  UserGroupIcon,
  PhotoIcon,
  SwatchIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

interface Project {
  _id: Id<"projects">;
  _creationTime: number;
  name: string;
  description?: string;
  type: "website" | "web-app" | "saas" | "mobile-app" | "games" | "brand-identity" | "feature-module" | "design-system" | "other";
  phase: "exploration" | "definition" | "specification" | "documentation";
  status: "active" | "archived" | "completed";
  referenceCount: number;
  collaboratorCount: number;
  themeCount: number;
}

interface ProjectCardProps {
  project: Project;
}

const PROJECT_TYPE_CONFIG = {
  website: { label: "Website", color: "bg-blue-100 text-blue-800" },
  "web-app": { label: "Web App", color: "bg-indigo-100 text-indigo-800" },
  saas: { label: "SaaS", color: "bg-cyan-100 text-cyan-800" },
  "mobile-app": { label: "Mobile App", color: "bg-purple-100 text-purple-800" },
  games: { label: "Games", color: "bg-red-100 text-red-800" },
  "brand-identity": { label: "Brand", color: "bg-pink-100 text-pink-800" },
  "feature-module": { label: "Feature", color: "bg-green-100 text-green-800" },
  "design-system": { label: "Design System", color: "bg-orange-100 text-orange-800" },
  other: { label: "Other", color: "bg-gray-100 text-gray-800" },
};

const PHASE_CONFIG = {
  exploration: { label: "Exploring", color: "bg-yellow-100 text-yellow-800", icon: PhotoIcon },
  definition: { label: "Defining", color: "bg-blue-100 text-blue-800", icon: SwatchIcon },
  specification: { label: "Specifying", color: "bg-purple-100 text-purple-800", icon: ChartBarIcon },
  documentation: { label: "Documenting", color: "bg-green-100 text-green-800", icon: FolderIcon },
};

export function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate();
  const typeConfig = PROJECT_TYPE_CONFIG[project.type];
  const phaseConfig = PHASE_CONFIG[project.phase];
  const PhaseIcon = phaseConfig.icon;

  const handleClick = () => {
    navigate(`/projects/${project._id}`);
  };

  const createdDate = new Date(project._creationTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      onClick={handleClick}
      className="card bg-info-light shadow-lg border-2 border-gray-800 hover:shadow-xl transition-shadow cursor-pointer group"
    >
      <div className="card-body p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="card-title text-gray-900 group-hover:text-primary transition-colors line-clamp-1 text-lg">
              {project.name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">Created {createdDate}</p>
          </div>
          <span
            className={`badge ml-2 px-2.5 py-0.5 ${typeConfig.color}`}
          >
            {typeConfig.label}
          </span>
        </div>

        {/* Description */}
        {project.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {project.description}
          </p>
        )}

        {/* Phase Badge */}
        <div className="mb-4">
          <span
            className={`badge badge-lg px-3 py-1 ${phaseConfig.color}`}
          >
            <PhaseIcon className="h-4 w-4 mr-1.5" />
            {phaseConfig.label}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t-2 border-gray-800">
          <div className="flex items-center">
            <PhotoIcon className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <div className="text-sm font-semibold text-gray-900">{project.referenceCount}</div>
              <div className="text-xs text-gray-500">References</div>
            </div>
          </div>
          <div className="flex items-center">
            <SwatchIcon className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <div className="text-sm font-semibold text-gray-900">{project.themeCount}</div>
              <div className="text-xs text-gray-500">Themes</div>
            </div>
          </div>
          <div className="flex items-center">
            <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <div className="text-sm font-semibold text-gray-900">{project.collaboratorCount}</div>
              <div className="text-xs text-gray-500">Team</div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
